import * as React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Fab from '@mui/material/Fab';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import Resizer from 'react-image-file-resizer';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import AppConfig from '../services/app-config';
import AuthService from '../services/auth';
import CustomSnackbar from '../components/CustomSnackbar';

const apiUrl = process.env.REACT_APP_MGO_API_URL;

export default function Packages({ setNavbarState, navbarState }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const role = AuthService.getCurrentUserRole();
  const isReceptor = role === 'receptor' || role === 'admin' || role === 'superuser';
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = React.useState(false);
  const [packages, setPackages] = React.useState([]);
  const [loadingPackages, setLoadingPackages] = React.useState(true);

  // Receptor form state
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [selectedUnit, setSelectedUnit] = React.useState(null); // {unit, friendly_name, residents}
  const [recipientName, setRecipientName] = React.useState('');
  const [recipientUsername, setRecipientUsername] = React.useState('');
  const [carrier, setCarrier] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [image, setImage] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [manualUnit, setManualUnit] = React.useState('');
  const [showManualUnit, setShowManualUnit] = React.useState(false);
  const [formOpen, setFormOpen] = React.useState(false);
  const [formStep, setFormStep] = React.useState(1); // 1 = search, 2 = details
  const fileInputRef = React.useRef(null);
  const debounceRef = React.useRef(null);

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: true, showOptions: false, showSearch: false, title: 'Paquetería' });
    fetchPackages();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchPackages = async () => {
    setLoadingPackages(true);
    try {
      const response = await apiClient.get('packages', {}, { action: 'fetch_packages' });
      setPackages(response.data.data || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' });
      } else {
        showSnackbar(getErrorMessage(error), 'error');
      }
    } finally {
      setLoadingPackages(false);
    }
  };

  // Debounced recipient search (receptor only)
  React.useEffect(() => {
    if (!isReceptor) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await apiClient.get(`packages/recipients?q=${encodeURIComponent(q)}`, {}, { action: 'search_recipients' });
        setSearchResults(response.data.data || []);
      } catch (error) {
        console.error('Error searching recipients:', error);
        if (shouldRedirectToLogin(error)) {
          setRedirect({ trigger: true, route: '/login' });
        }
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [searchQuery, isReceptor]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('La imagen excede el tamaño máximo de 5MB.', 'error');
      e.target.value = '';
      return;
    }
    Resizer.imageFileResizer(
      file,
      800,
      800,
      'JPEG',
      80,
      0,
      (compressedFile) => {
        setImage(compressedFile);
        setImagePreview(URL.createObjectURL(compressedFile));
      },
      'blob'
    );
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const selectUnit = (unitObj) => {
    setSelectedUnit(unitObj);
    setSearchQuery('');
    setSearchResults([]);
    setShowManualUnit(false);
    setManualUnit('');
    if (unitObj.residents && unitObj.residents.length > 0) {
      const r = unitObj.residents[0];
      setRecipientName(`${r.name} ${r.lastname}`.trim());
      setRecipientUsername(r.username || '');
    } else {
      setRecipientName('');
      setRecipientUsername('');
    }
  };

  const clearSelectedUnit = () => {
    setSelectedUnit(null);
    setRecipientName('');
    setRecipientUsername('');
    setCarrier('');
    setDescription('');
    clearImage();
  };

  const resetFormState = () => {
    clearSelectedUnit();
    setSearchQuery('');
    setSearchResults([]);
    setShowManualUnit(false);
    setManualUnit('');
  };

  const useManualUnit = () => {
    setShowManualUnit(true);
    setSelectedUnit(null);
    setSearchResults([]);
    setSearchQuery('');
  };

  const submitPackage = async () => {
    const unit = selectedUnit ? selectedUnit.unit : (manualUnit || '').trim();
    if (!unit) {
      showSnackbar('Selecciona o ingresa una unidad.', 'error');
      return;
    }
    if (!recipientName.trim()) {
      showSnackbar('Ingresa el nombre del destinatario.', 'error');
      return;
    }
    if (!image) {
      showSnackbar('Toma una foto del paquete.', 'error');
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append('unit', unit);
    formData.append('recipient_name', recipientName.trim());
    if (recipientUsername) formData.append('recipient_username', recipientUsername);
    if (carrier) formData.append('carrier', carrier);
    if (description) formData.append('description', description);
    formData.append('image', image);
    try {
      const response = await apiClient.post('packages', formData, {}, { action: 'create_package' });
      showSnackbar(`Paquete registrado. ${response.data.data.notified_count} residente(s) notificado(s).`, 'success');
      clearSelectedUnit();
      setFormOpen(false);
      setShowManualUnit(false);
      setManualUnit('');
      fetchPackages();
    } catch (error) {
      console.error('Error creating package:', error);
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' });
      } else {
        showSnackbar(getErrorMessage(error), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const markPickedUp = async (packageId) => {
    try {
      await apiClient.post('packages/pickup', { package_id: packageId }, {}, { action: 'pickup_package' });
      showSnackbar('Paquete marcado como entregado.', 'success');
      fetchPackages();
    } catch (error) {
      console.error('Error picking up package:', error);
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' });
      } else {
        showSnackbar(getErrorMessage(error), 'error');
      }
    }
  };

  const formatTs = (ts) => {
    if (!ts) return '';
    return AppConfig.formatFriendlyDate(ts);
  };

  const renderPhoto = (photo) => {
    if (!photo) return null;
    return `${apiUrl}/img/packages/${photo}`;
  };

  const statusBadge = (status) => {
    if (status === 'entregado') {
      return <Chip size="small" color="success" label="Entregado" icon={<CheckCircleIcon />} sx={{ fontWeight: 700 }} />;
    }
    return <Chip size="small" color="warning" label="Recibido" sx={{ fontWeight: 700 }} />;
  };

  const renderPackageCard = (pkg) => (
    <Card
      key={pkg.id}
      sx={{
        mb: 2,
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.shadows[1],
      }}
    >
      <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', p: 2, '&:last-child': { pb: 2 } }}>
        {pkg.photo && (
          <Box
            component="img"
            src={renderPhoto(pkg.photo)}
            alt="Paquete"
            sx={{
              width: 80,
              height: 80,
              borderRadius: 2,
              objectFit: 'cover',
              flexShrink: 0,
              bgcolor: alpha(theme.palette.text.primary, 0.05),
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: 'text.primary' }} noWrap>
              {pkg.recipient_name}
            </Typography>
            {statusBadge(pkg.status)}
          </Stack>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {AppConfig.get('unit_type')} {pkg.unit}
          </Typography>
          {pkg.carrier && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Courier: {pkg.carrier}</Typography>
          )}
          {pkg.description && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{pkg.description}</Typography>
          )}
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
            Recibido {formatTs(pkg.ts)}
          </Typography>
          {pkg.status === 'entregado' && pkg.picked_up_ts && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Entregado {formatTs(pkg.picked_up_ts)}{pkg.picked_up_by_name ? ` por ${pkg.picked_up_by_name}` : ''}
            </Typography>
          )}
          {isReceptor && pkg.status === 'recibido' && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              sx={{ mt: 1, borderRadius: 2, textTransform: 'none' }}
              onClick={() => markPickedUp(pkg.id)}
            >
              Marcar entregado
            </Button>
          )}
          {!isReceptor && pkg.status === 'recibido' && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              sx={{ mt: 1, borderRadius: 2, textTransform: 'none' }}
              onClick={() => markPickedUp(pkg.id)}
            >
              Recibí el paquete
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace />;
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 3, pb: 12 }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          mx: -3,
          px: 3,
          pt: 2,
          pb: 1.5,
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              width: 40,
              height: 40,
              ml: -1,
              color: 'text.primary',
              '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08) },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 28 }} />
          </IconButton>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>Paquetería</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: '10px',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
              color: 'text.secondary',
            }}
          >
            <LocalShippingIcon sx={{ fontSize: 15, color: 'secondary.main' }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
              {isReceptor ? 'Recepción' : 'Mis paquetes'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />

      {isReceptor && !loadingPackages && packages.length > 0 && (
        <Divider sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>Paquetes de hoy</Typography>
        </Divider>
      )}

      {loadingPackages ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : packages.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <LocalShippingIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, color: 'text.secondary' }}>
            {isReceptor ? 'Aún no hay paquetes registrados.' : 'No tienes paquetes pendientes.'}
          </Typography>
        </Box>
      ) : (
        <Box>{packages.map(renderPackageCard)}</Box>
      )}

      {isReceptor && (
        <Fab
          aria-label="add"
          onClick={() => { resetFormState(); setFormOpen(true); setFormStep(1); }}
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 24,
            width: 56,
            height: 56,
            minHeight: 0,
            borderRadius: '999px',
            backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.primary.main : '#021545'),
            color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.getContrastText(theme.palette.primary.main) : 'white'),
            boxShadow: '0 10px 25px rgba(2,21,69,0.25)',
            '&:hover': {
              backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.primary.dark : '#021545'),
            },
          }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
        </Fab>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Registrar paquete</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>
              Paso {formStep} de 2{formStep === 1 ? ': Buscar destinatario' : ': Detalles del paquete'}
            </Typography>
          </Box>
          <IconButton onClick={() => setFormOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* STEP 1: search */}
          {formStep === 1 && (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por unidad o residente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 1.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searching ? <CircularProgress size={18} /> : null,
                }}
              />
              {searchResults.length > 0 && (
                <Stack spacing={1} sx={{ mb: 1.5 }}>
                  {searchResults.map((u) => (
                    <Box
                      key={u.unit}
                      onClick={() => selectUnit(u)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.08) },
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'text.primary' }}>
                        {AppConfig.get('unit_type')} {u.unit}
                      </Typography>
                      {u.friendly_name && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{u.friendly_name}</Typography>
                      )}
                      {u.residents.length > 0 && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          {u.residents.map((r) => `${r.name} ${r.lastname}`.trim()).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
              {searchQuery.trim().length >= 2 && !searching && searchResults.length === 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WarningAmberIcon color="warning" fontSize="small" />
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                      Sin resultados. Puedes ingresar la unidad manualmente.
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={useManualUnit}>
                    Ingresar unidad manualmente
                  </Button>
                </Box>
              )}
              {showManualUnit && (
                <Box sx={{ mb: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Unidad"
                    value={manualUnit}
                    onChange={(e) => setManualUnit(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Button size="small" onClick={() => { setShowManualUnit(false); setManualUnit(''); }}>
                    Volver a buscar
                  </Button>
                </Box>
              )}
              {selectedUnit && (
                <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.secondary.main, 0.08) }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'text.primary' }}>
                        {AppConfig.get('unit_type')} {selectedUnit.unit}
                      </Typography>
                      {selectedUnit.friendly_name && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{selectedUnit.friendly_name}</Typography>
                      )}
                    </Box>
                    <IconButton size="small" onClick={clearSelectedUnit}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              )}
            </>
          )}
          {/* STEP 2: details */}
          {formStep === 2 && (
            <>
              {selectedUnit && (
                <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.secondary.main, 0.08) }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'text.primary' }}>
                        {AppConfig.get('unit_type')} {selectedUnit.unit}
                      </Typography>
                      {selectedUnit.friendly_name && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{selectedUnit.friendly_name}</Typography>
                      )}
                    </Box>
                    <IconButton size="small" onClick={clearSelectedUnit}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              )}
              {showManualUnit && (
                <Box sx={{ mb: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Unidad"
                    value={manualUnit}
                    onChange={(e) => setManualUnit(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Button size="small" onClick={() => { setShowManualUnit(false); setManualUnit(''); }}>
                    Volver a buscar
                  </Button>
                </Box>
              )}
              <TextField
                fullWidth
                size="small"
                label="Nombre del destinatario"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Courier (opcional)"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Descripción (opcional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                minRows={2}
                sx={{ mb: 1.5 }}
              />

              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', mb: 1 }}>
                Foto del paquete
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              {!imagePreview ? (
                <Box
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  sx={{
                    width: '100%',
                    py: 3,
                    borderRadius: 2,
                    border: '2px dashed',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.05) },
                  }}
                >
                  <AddAPhotoIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Tomar foto</Typography>
                </Box>
              ) : (
                <Box sx={{ position: 'relative', mb: 1.5 }}>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{ width: '100%', borderRadius: 2, maxHeight: 300, objectFit: 'cover' }}
                  />
                  <IconButton
                    onClick={clearImage}
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          {formStep === 1 ? (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              disabled={!selectedUnit && !(showManualUnit && manualUnit.trim())}
              onClick={() => setFormStep(2)}
              sx={{ height: 48, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
            >
              Continuar
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
              <Button
                variant="text"
                color="primary"
                onClick={() => setFormStep(1)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
              >
                Atrás
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                disabled={loading || !image}
                onClick={submitPackage}
                sx={{ height: 48, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
              >
                {loading ? <CircularProgress size={22} /> : 'Registrar paquete'}
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}
